import { useSearchParams, usePathname } from 'next/navigation';
import { useCategories } from '@/entities/category/model/use-categories';
import { useUserWishlist } from '@/shared/providers/user-provider/use-user';
import { useGameApi } from '@/entities/game/model/api';
import { useIsNotifyLoading, useIsBonusActive, useBonusInfoGames } from '@/shared/providers/notifications-provider/store';
import { useMemo } from 'react';
import { useBonuses } from '@/entities/bonus/model/use-bonuses';
import {
  ALL_FILTER,
  BONUS_FILTER,
  matchesTypeOrProvider,
  matchesCategory,
  matchesSearch,
  matchesWishlist,
  getWishlistSet,
  getGameById,
  getFilteredGames,
} from '@/entities/game/model/helpers';
import { getBonusGamesSet } from '@/shared/libs/get-bonus-games-set';
import { getGamesByFilter } from '@/entities/game/model/get-games-by-filter';

// Константы
const VALID_GAME_TYPES = ['slots', 'instant', 'table', 'live', 'lottery'];
const WISHLIST_IDENTIFIER = 'favorite';
const NEW_FILTER = 'new';
const BONUS_CATEGORY_ID = 'bonus';
const URL_REGEX = /\/games\/([^\/]+)/;

export function useGameSections() {
  const wishlist = useUserWishlist();
  const isNotifyLoading = useIsNotifyLoading();
  const isBonusActive = useIsBonusActive();
  const bonusGames = useBonusInfoGames();
  const { categories, isLoading: isCategoriesLoading } = useCategories();
  const { gameData, isGamesLoading, recommendedData, isRecommendedLoading } = useGameApi();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    data: bonusData,
    isLoading:isLoadingBonus,
  } = useBonuses();
  const activeBonus=bonusData?.find(el=>el?.state==="active")
  const activeBonusId=activeBonus?.games?.length===1 ? activeBonus?.games?.at(-1): null

  // параметры URL
  const { filterParam, searchParam, gameTypeOrProvider } = useMemo(() => {
    const filter = searchParams.get('filter');
    const search = searchParams.get('search');
    const gameTypeOrProviderMatch = pathname.match(URL_REGEX)?.[1];
    
    return {
      filterParam: filter,
      searchParam: search,
      gameTypeOrProvider: gameTypeOrProviderMatch
    };
  }, [searchParams, pathname]);

  // флаги состояния фильтров
  const { isTypeSelected, isWishlist, isProviderSelected, isCategorySelected, isSearchTyped } = useMemo(() => {
    const isTypeSelected = VALID_GAME_TYPES.includes(gameTypeOrProvider ?? '');
    const isWishlist = gameTypeOrProvider === WISHLIST_IDENTIFIER;
    const isProviderSelected = gameTypeOrProvider && !isTypeSelected && !isWishlist;
    const isCategorySelected = filterParam && filterParam !== ALL_FILTER;
    const isSearchTyped = searchParam && searchParam !== '';

    return {
      isTypeSelected,
      isWishlist,
      isProviderSelected,
      isCategorySelected,
      isSearchTyped
    };
  }, [gameTypeOrProvider, filterParam, searchParam]);

  // рекомендуемые игры
  const { recommendedGame, preferredGame, newGame } = useMemo(() => {
    const gameById = getGameById(gameData);

    const recommendedGame = recommendedData?.recommended
      ?.map(gameId => gameById.get(gameId))
      ?.filter(Boolean) || [];

    const preferredGame = recommendedData?.preferred
      ?.map(gameId => gameById.get(gameId))
      ?.filter(Boolean) || [];

    const newGame = getGamesByFilter(gameData).get(NEW_FILTER) ?? [];

    return { recommendedGame, preferredGame, newGame };
  }, [recommendedData, gameData]);

  // Мемоизированные категории с играми
  const categoriesWithGames = useMemo(() => {
    if (!categories || !gameData) return [];

    const wishlistSet = getWishlistSet(wishlist);
    const bonusGamesSet = getBonusGamesSet(bonusGames);
    const categoryIds = new Set(categories.map((c) => c.id));
    const gamesByCategory = new Map(categories.map((c) => [c.id, [] as any[]]));

    for (const game of gameData) {
      if (
        !matchesTypeOrProvider(game, { isTypeSelected, isProviderSelected, gameTypeOrProvider }) ||
        !matchesSearch(game, { isSearchTyped, searchParam }) ||
        !matchesWishlist(game, { isWishlist, wishlistSet })
      ) {
        continue;
      }

      for (const filterId of new Set(game?.filters || [])) {
        if (filterId !== BONUS_CATEGORY_ID && categoryIds.has(filterId)) {
          gamesByCategory.get(filterId).push(game);
        }
      }
    }

    if (categoryIds.has(BONUS_CATEGORY_ID)) {
      gamesByCategory.set(BONUS_CATEGORY_ID, gameData.filter((game) => bonusGamesSet.has(game.id)));
    }

    return categories.map((category) => ({
      ...category,
      games: gamesByCategory.get(category.id) ?? [],
    }));
  }, [categories, gameData, bonusGames, isTypeSelected, isProviderSelected, gameTypeOrProvider, isSearchTyped, searchParam, isWishlist, wishlist]);

  // список отфильтрованных игр
  const filteredGames = getFilteredGames(() => {
    if (!gameData) return [];

    const wishlistSet = getWishlistSet(wishlist);
    const bonusGamesSet = getBonusGamesSet(bonusGames);

    const isBonusCategory = filterParam === BONUS_FILTER && isBonusActive;
    const source =
      filterParam && isCategorySelected && !isBonusCategory
        ? (getGamesByFilter(gameData).get(filterParam) ?? [])
        : gameData;

    const games = source.filter((game) =>
      matchesTypeOrProvider(game, { isTypeSelected, isProviderSelected, gameTypeOrProvider }) &&
      matchesCategory(game, { isCategorySelected, filterParam, isBonusActive, bonusGamesSet }) &&
      matchesSearch(game, { isSearchTyped, searchParam }) &&
      matchesWishlist(game, { isWishlist, wishlistSet })
    );

    // Если есть activeBonusId и выбран тип slots, сортируем игры
    if (activeBonusId && isTypeSelected && gameTypeOrProvider === 'slots') {
      return [...games].sort((a, b) => {
        if (a.id === activeBonusId) return -1;
        if (b.id === activeBonusId) return 1;
        return 0;
      });
    }

    return games;
  }, [gameData, isTypeSelected, isProviderSelected, gameTypeOrProvider, isCategorySelected, filterParam, isBonusActive, bonusGames, isSearchTyped, searchParam, isWishlist, wishlist, activeBonusId]);

  // флаг показа категорий
  const showCategories = useMemo(() => {
    return !isTypeSelected &&
      !isProviderSelected &&
      !isCategorySelected &&
      !isSearchTyped &&
      !isWishlist;
  }, [isTypeSelected, isProviderSelected, isCategorySelected, isSearchTyped, isWishlist]);

  // Проверка загрузки
  const isSectionsLoading = useMemo(() => {
    return isNotifyLoading ||
      isCategoriesLoading ||
      isGamesLoading ||
      isRecommendedLoading ||
      isLoadingBonus;
  }, [isNotifyLoading, isCategoriesLoading, isGamesLoading, isRecommendedLoading, isLoadingBonus]);

  if (isSectionsLoading) {
    return {
      isSectionsLoading: true,
    };
  }

  return {
    categoriesWithGames,
    showCategories,
    games: filteredGames,
    gamesData: gameData,
    isSectionsLoading: false,
    recommendedGame,
    preferredGame,
    newGame,
    activeBonusId: gameTypeOrProvider === 'slots' ? activeBonusId: null
  };
}
